import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Sortable from "sortablejs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message, Modal, Input, Spin, Tag } from "antd";
import { X, Plus, Edit2, Trash2 } from "react-feather";
import type { AxiosError } from "axios";

import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../api/ApiRoutes";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import {
  getApiErrorMessage,
  getApiSuccessMessage,
} from "../../../../utils/ErrorUtils";
import { useProductFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { TRIVELTA_PRODUCTS } from "../../../../utils/products";
import { useSportsbookFm } from "../SportsbookFmContext";
import { RenameEntityModal } from "../Translations/RenameEntityModal";
import { TranslationField } from "../Translations/TranslationField";
import { useSportsbookTranslationEditor } from "../Translations/useSportsbookTranslationEditor";
import {
  getListEntityBaseName,
  toListTranslationEditorData,
  toTranslationValueEntries,
} from "../Translations/listTranslationData";

import ClipPath from "../../../../assets/icons/ic_clip-path.svg";
import "./stylesEventMarkets.css";

import {
  buildTabOrderMap,
  cardRowDisplayOrder,
  groupCardsByTabId,
  mapRawCardsForTab,
  mapRawMarketOptionsToMarketOptions,
  mapRawSportsToSports,
  mapRawTabsToTabs,
  parseTokenFromStorage,
  sortAggregatedCards,
  sortTabsByOrder,
  tabsListToSignature,
} from "./eventMarketsTransforms";
import {
  ENTITY_TYPES,
  type Card,
  type CreateCardVariables,
  type DeleteCardTarget,
  type EntityType,
  type MarketOption,
  type MarketCardRowHandler,
  type RawCard,
  type RawMarketOption,
  type RawSport,
  type RawTab,
  type RenameMarketPayload,
  type BundledTranslationChanges,
  type RenameMarketVariables,
  type ReorderCardsVariables,
  type Sport,
  type Tab,
} from "./eventMarketsTypes";

type ApiError = AxiosError<{ message?: string } | undefined>;
type PrivateAdminApi = ReturnType<typeof createPrivateAdminPanelInstance>;

interface CardsTableRowsProps {
  cards: Card[];
  isLoading: boolean;
  onRename: MarketCardRowHandler;
  onDelete: MarketCardRowHandler;
  canEdit?: boolean;
}

function EventMarkets(): React.ReactElement {
  const { canEdit } = useSportsbookFm();
  const token = parseTokenFromStorage(localStorage.getItem("token"));

  const api = useMemo<PrivateAdminApi>(
    () => createPrivateAdminPanelInstance(token?.AccessToken),
    [token?.AccessToken]
  );
  const qc = useQueryClient();

  const [entityType, setEntityType] = useState<EntityType>(
    ENTITY_TYPES.PREMATCH
  );
  const [activeSportId, setActiveSportId] = useState<Sport["id"] | null>(null);
  const [selectedCardsTabId, setSelectedCardsTabId] = useState<string | null>(
    null
  ); // selected tab for Markets tables

  const [showAddTabModal, setShowAddTabModal] = useState<boolean>(false);
  const [newTabName, setNewTabName] = useState<string>("");
  const [editTabId, setEditTabId] = useState<string | null>(null);
  const [editTabName, setEditTabName] = useState<string>("");
  const [originalEditTabName, setOriginalEditTabName] = useState<string>("");

  const [showAddCardModal, setShowAddCardModal] = useState<boolean>(false);
  const [targetTabForAddCard, setTargetTabForAddCard] = useState<string | null>(
    null
  ); // tabId
  const [addMarketQuery, setAddMarketQuery] = useState<string>("");
  const [addMarketSelected, setAddMarketSelected] =
    useState<MarketOption | null>(null);
  const [debouncedMarketQuery, setDebouncedMarketQuery] = useState<string>("");

  const [showRenameMarketModal, setShowRenameMarketModal] =
    useState<boolean>(false);
  const [renameMarketPayload, setRenameMarketPayload] =
    useState<RenameMarketPayload | null>(null); // {sport_id, market_id, current_display_name}
  const [renameMarketEntityKey, setRenameMarketEntityKey] = useState<
    string | undefined
  >();

  const [deleteTabId, setDeleteTabId] = useState<string | null>(null); // tabId or null
  const [deleteCardTarget, setDeleteCardTarget] =
    useState<DeleteCardTarget | null>(null); // { tabId, marketId } or null

  const entityTypeLabel =
    entityType === ENTITY_TYPES.LIVE ? AppStrings.LIVE : AppStrings.PRE_MATCH;
  const entityTypeLabelLower = useMemo(
    () => entityTypeLabel.toLowerCase().replace("-", " "),
    [entityTypeLabel]
  );
  const { isEnabled: translationsFlagEnabled } = useProductFeatureFlag(
    "fm_translations",
    TRIVELTA_PRODUCTS.SPORTSBOOK
  );
  const translationsEnabled = translationsFlagEnabled === true;

  // Sports list (top row)
  const sportsQuery = useQuery<Sport[], ApiError>({
    queryKey: ["event-markets", "sports", entityType],
    queryFn: async () => {
      const res = await api.post(API_ROUTES.GET_EVENT_MARKETS_SPORTS, {
        entity_type: entityType,
      });
      const sports = (res?.data?.sports || []) as RawSport[];
      return mapRawSportsToSports(sports);
    },
    refetchOnWindowFocus: false,
  });

  const activeSportName = useMemo(() => {
    const sport = (sportsQuery.data ?? []).find((s) => s.id === activeSportId);
    return sport?.name || AppStrings.SPORT.toLowerCase();
  }, [sportsQuery.data, activeSportId]);

  // set default active sport
  useEffect(() => {
    if (!sportsQuery.isLoading && sportsQuery.data && sportsQuery.data.length) {
      if (
        !activeSportId ||
        !sportsQuery.data.find((s) => s.id === activeSportId)
      ) {
        setActiveSportId(sportsQuery.data[0].id);
      }
    }
  }, [sportsQuery.isLoading, sportsQuery.data, activeSportId]);

  // Tabs for active sport
  const tabsQuery = useQuery<Tab[], ApiError>({
    queryKey: ["event-markets", "tabs", entityType, activeSportId || "none"],
    enabled: !!activeSportId,
    queryFn: async () => {
      const res = await api.post(API_ROUTES.GET_MARKET_ENTITIES_BY_TYPE, {
        entity_type: entityType,
        sport_id: activeSportId,
        item_type: "TAB",
        parent_type: "SPORT",
        parent_id: activeSportId,
      });
      const items = (res?.data?.entities_data || []) as RawTab[];
      return mapRawTabsToTabs(items);
    },
    refetchOnWindowFocus: false,
  });

  const tabsList = useMemo(
    () => sortTabsByOrder(tabsQuery.data ?? []),
    [tabsQuery.data]
  );
  const editingTab = editTabId
    ? tabsList.find((tab) => tab.tab_id === editTabId)
    : undefined;
  const tabTranslationEntityKey =
    translationsEnabled && editTabId
      ? (editingTab?.translations?.key ?? editingTab?.entity_id)
      : undefined;
  const tabInitialTranslationData = useMemo(
    () =>
      translationsEnabled &&
      editingTab &&
      toTranslationValueEntries(editingTab).length > 0
        ? toListTranslationEditorData(
            editingTab,
            tabTranslationEntityKey,
            editingTab.name
          )
        : undefined,
    [editingTab, tabTranslationEntityKey, translationsEnabled]
  );
  const tabTranslationEditor = useSportsbookTranslationEditor({
    entityKey: tabTranslationEntityKey,
    enabled: translationsEnabled,
    initialData: tabInitialTranslationData,
  });
  const renameMarketTranslationData = useMemo(
    () =>
      translationsEnabled &&
      renameMarketPayload &&
      toTranslationValueEntries(renameMarketPayload).length > 0
        ? toListTranslationEditorData(
            renameMarketPayload,
            renameMarketEntityKey,
            renameMarketPayload.current_display_name ?? ""
          )
        : undefined,
    [
      renameMarketEntityKey,
      renameMarketPayload,
      translationsEnabled,
    ]
  );

  const deleteTabName = useMemo(() => {
    if (!deleteTabId) return "";
    const tab = tabsList.find((t) => t.tab_id === deleteTabId);
    return tab?.name || "";
  }, [deleteTabId, tabsList]);
  useEffect(() => {
    if (!tabsList.length) {
      setSelectedCardsTabId(null);
      return;
    }
    // If no selected tab, default to first
    if (!selectedCardsTabId) setSelectedCardsTabId(tabsList[0].tab_id);
    // If selected tab no longer exists, reset to first
    else if (!tabsList.find((t) => t.tab_id === selectedCardsTabId))
      setSelectedCardsTabId(tabsList[0].tab_id);
  }, [tabsList, selectedCardsTabId]);

  const tabsSignature = useMemo(() => tabsListToSignature(tabsList), [tabsList]);

  const allCardsQuery = useQuery<Card[], ApiError>({
    queryKey: [
      "event-markets",
      "cards",
      "aggregate",
      entityType,
      activeSportId || "none",
      tabsSignature,
    ],
    enabled: !!activeSportId,
    queryFn: async () => {
      if (!tabsList.length) return [];
      const tabOrderMap = buildTabOrderMap(tabsList);

      const responses = await Promise.all(
        tabsList.map(async (tab, tabIndex) => {
          const res = await api.post(API_ROUTES.GET_MARKET_ENTITIES_BY_TYPE, {
            entity_type: entityType,
            sport_id: activeSportId,
            item_type: "CARD",
            parent_type: "TAB",
            parent_id: tab.tab_id,
          });
          const items = (res?.data?.entities_data || []) as RawCard[];
          return mapRawCardsForTab(items, tab, tabOrderMap, tabIndex);
        })
      );

      return sortAggregatedCards(responses.flat());
    },
    refetchOnWindowFocus: false,
  });

  const cardsByTabId = useMemo(
    () => groupCardsByTabId(allCardsQuery.data ?? []),
    [allCardsQuery.data]
  );

  // Reorder tabs
  const reorderTabsMutation = useMutation<unknown, ApiError, string[]>({
    mutationFn: async (orderedTabIds: string[]) => {
      const res = await api.post(API_ROUTES.REORDER_MARKET_ENTITIES, {
        entity_type: entityType,
        sport_id: activeSportId,
        parent_type: "SPORT",
        item_type: "TAB",
        parent_id: activeSportId,
        ordered_ids: orderedTabIds,
      });
      return res.data;
    },
    onSuccess: (response) => {
      qc.invalidateQueries({
        queryKey: ["event-markets", "tabs", entityType, activeSportId],
      });
      message.success(
        getApiSuccessMessage(response, AppStrings.TAB_ORDER_UPDATED)
      );
    },
    onError: (error) =>
      message.error(
        getApiErrorMessage(error, AppStrings.FAILED_TO_UPDATE_TAB_ORDER)
      ),
  });

  // Reorder cards within tab
  const reorderCardsMutation = useMutation<
    unknown,
    ApiError,
    ReorderCardsVariables
  >({
    mutationFn: async ({ tabId, orderedMarketIds }: ReorderCardsVariables) => {
      const res = await api.post(API_ROUTES.REORDER_MARKET_ENTITIES, {
        entity_type: entityType,
        sport_id: activeSportId,
        parent_type: "TAB",
        parent_id: tabId,
        item_type: "CARD",
        ordered_ids: orderedMarketIds,
      });
      return res.data;
    },
    onSuccess: (response) => {
      qc.invalidateQueries({ queryKey: ["event-markets", "cards"] });
      message.success(
        getApiSuccessMessage(response, AppStrings.CARD_ORDER_UPDATED)
      );
    },
    onError: (error) =>
      message.error(
        getApiErrorMessage(error, AppStrings.FAILED_TO_UPDATE_CARD_ORDER)
      ),
  });

  // Create tab
  const createTabMutation = useMutation<unknown, ApiError, string>({
    mutationFn: async (tabName: string) => {
      const res = await api.post(API_ROUTES.CREATE_NEW_MARKET_TAB, {
        entity_type: entityType,
        sport_id: activeSportId,
        tab_name: tabName,
      });
      return res.data;
    },
    onSuccess: (response) => {
      setShowAddTabModal(false);
      setNewTabName("");
      qc.invalidateQueries({
        queryKey: ["event-markets", "tabs", entityType, activeSportId],
      });
      message.success(getApiSuccessMessage(response, AppStrings.TAB_CREATED));
    },
    onError: (error) =>
      message.error(getApiErrorMessage(error, AppStrings.FAILED_TO_CREATE_TAB)),
  });

  const updateTabMutation = useMutation<
    unknown,
    ApiError,
    {
      tabId: string;
      tabName: string;
      translationChanges?: BundledTranslationChanges;
    }
  >({
    mutationFn: async ({ tabId, tabName, translationChanges }) => {
      const res = await api.patch(API_ROUTES.UPDATE_MARKET_TAB, {
        entity_type: entityType,
        sport_id: activeSportId,
        tab_id: tabId,
        tab_name: tabName,
        ...(translationChanges
          ? {
              translation_key: translationChanges.translation_key,
              translations: translationChanges.translations,
            }
          : {}),
      });
      return res.data;
    },
    onSuccess: (response) => {
      setEditTabId(null);
      setEditTabName("");
      setOriginalEditTabName("");
      tabTranslationEditor.reset();
      qc.invalidateQueries({
        queryKey: ["event-markets", "tabs", entityType, activeSportId],
      });
      message.success(getApiSuccessMessage(response, AppStrings.TAB_UPDATED));
    },
    onError: (error) =>
      message.error(getApiErrorMessage(error, AppStrings.FAILED_TO_UPDATE_TAB)),
  });

  // Delete tab
  const deleteTabMutation = useMutation<unknown, ApiError, string>({
    mutationFn: async (tabId: string) => {
      if (!activeSportId) {
        throw new Error(AppStrings.NO_ACTIVE_SPORT_SELECTED);
      }
      const res = await api.delete(API_ROUTES.DELETE_MARKET_TAB, {
        data: {
          entity_type: entityType,
          sport_id: activeSportId,
          tab_id: tabId,
        },
      });
      return res.data;
    },
    onSuccess: (response) => {
      qc.invalidateQueries({
        queryKey: ["event-markets", "tabs", entityType, activeSportId],
      });
      message.success(getApiSuccessMessage(response, AppStrings.TAB_DELETED));
      setDeleteTabId(null);
    },
    onError: (error) =>
      message.error(getApiErrorMessage(error, AppStrings.FAILED_TO_DELETE_TAB)),
  });

  // Create card
  const createCardMutation = useMutation<
    unknown,
    ApiError,
    CreateCardVariables
  >({
    mutationFn: async ({ tabId, marketId }: CreateCardVariables) => {
      const res = await api.post(API_ROUTES.CREATE_NEW_MARKET_CARD, {
        entity_type: entityType,
        sport_id: activeSportId,
        tab_id: tabId,
        market_id: marketId,
      });
      return res.data;
    },
    onSuccess: (response) => {
      setShowAddCardModal(false);
      setAddMarketSelected(null);
      setAddMarketQuery("");
      setTargetTabForAddCard(null);
      setDebouncedMarketQuery("");
      qc.invalidateQueries({ queryKey: ["event-markets", "cards"] });
      qc.invalidateQueries({
        queryKey: ["event-markets", "tabs", entityType, activeSportId],
      });
      message.success(
        getApiSuccessMessage(response, AppStrings.CARD_ADDED_TO_TAB)
      );
    },
    onError: (error) =>
      message.error(getApiErrorMessage(error, AppStrings.FAILED_TO_ADD_CARD)),
  });

  // Delete card
  const deleteCardMutation = useMutation<unknown, ApiError, DeleteCardTarget>({
    mutationFn: async ({ tabId, marketId }: DeleteCardTarget) => {
      if (!activeSportId) {
        throw new Error(AppStrings.NO_ACTIVE_SPORT_SELECTED);
      }
      const res = await api.delete(API_ROUTES.DELETE_MARKET_CARD, {
        data: {
          entity_type: entityType,
          sport_id: activeSportId,
          tab_id: tabId,
          market_id: marketId,
        },
      });
      return res.data;
    },
    onSuccess: (response) => {
      qc.invalidateQueries({ queryKey: ["event-markets", "cards"] });
      qc.invalidateQueries({
        queryKey: ["event-markets", "tabs", entityType, activeSportId],
      });
      message.success(getApiSuccessMessage(response, AppStrings.CARD_DELETED));
      setDeleteCardTarget(null);
    },
    onError: (error) =>
      message.error(getApiErrorMessage(error, AppStrings.FAILED_TO_DELETE_CARD)),
  });

  // Rename market (display name)
  const renameMarketMutation = useMutation<
    unknown,
    ApiError,
    RenameMarketVariables
  >({
    mutationFn: async ({
      marketId,
      newDisplayName,
      translationChanges,
    }: RenameMarketVariables) => {
      const res = await api.patch(API_ROUTES.RENAME_MARKET, {
        entity_type: entityType,
        sport_id: activeSportId,
        market_id: marketId,
        new_display_name: newDisplayName,
        ...(translationChanges
          ? {
              translation_key: translationChanges.translation_key,
              translations: translationChanges.translations,
            }
          : {}),
      });
      return res.data;
    },
    onSuccess: (response) => {
      qc.invalidateQueries({ queryKey: ["event-markets", "cards"] });
      qc.invalidateQueries({
        queryKey: ["event-markets", "tabs", entityType, activeSportId],
      });
      setShowRenameMarketModal(false);
      setRenameMarketPayload(null);
      setRenameMarketEntityKey(undefined);
      message.success(getApiSuccessMessage(response, AppStrings.MARKET_RENAMED));
    },
    onError: (error) =>
      message.error(
        getApiErrorMessage(error, AppStrings.FAILED_TO_RENAME_MARKET)
      ),
  });

  // Autocomplete markets with debounced search and cached results
  useEffect(() => {
    if (!showAddCardModal) {
      setDebouncedMarketQuery("");
      return;
    }
    const trimmed = addMarketQuery.trim();
    if (trimmed.length < 2) {
      setDebouncedMarketQuery("");
      return;
    }
    const handler = setTimeout(() => {
      setDebouncedMarketQuery((prev) => (prev === trimmed ? prev : trimmed));
    }, 300);
    return () => clearTimeout(handler);
  }, [addMarketQuery, showAddCardModal, entityType, activeSportId]);

  useEffect(() => {
    if (!showAddCardModal) return;
    setDebouncedMarketQuery("");
  }, [entityType, activeSportId, showAddCardModal]);

  const autocompleteQuery = useQuery<MarketOption[], ApiError>({
    queryKey: [
      "event-markets",
      "autocomplete",
      entityType,
      activeSportId || "none",
      debouncedMarketQuery || "",
    ],
    enabled:
      Boolean(api) &&
      Boolean(activeSportId) &&
      Boolean(showAddCardModal) &&
      debouncedMarketQuery.length >= 2,
    queryFn: async () => {
      if (activeSportId == null) return [];
      const res = await api.post(API_ROUTES.GET_SPORT_MARKETS_AUTOCOMPLETE, {
        entity_type: entityType,
        sport_id: activeSportId,
        search_term: debouncedMarketQuery,
        limit: 15,
      });
      const items = (res?.data?.data || []) as RawMarketOption[];
      return mapRawMarketOptionsToMarketOptions(items, activeSportId);
    },
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const addMarketOptions = autocompleteQuery.data ?? [];
  const addMarketLoading = autocompleteQuery.isFetching;
  const addMarketError = autocompleteQuery.isError
    ? getApiErrorMessage(
        autocompleteQuery.error,
        autocompleteQuery.error?.message || AppStrings.FAILED_TO_SEARCH
      )
    : null;
  const trimmedAddMarketQuery = addMarketQuery.trim();
  const showAutocompleteDropdown = trimmedAddMarketQuery.length >= 2;

  // Sortables refs
  const tabsTbodyRef = useRef<HTMLTableSectionElement | null>(null);
  const tabsSortableRef = useRef<Sortable | null>(null);
  const cardsSortableRefs = useRef<Record<string, Sortable | null>>({});

  useEffect(() => {
    setSelectedCardsTabId(null);
  }, [entityType]);

  useEffect(() => {
    setSelectedCardsTabId(null);
  }, [activeSportId]);

  useEffect(() => {
    return () => {
      Object.values(cardsSortableRefs.current).forEach((instance) => {
        try {
          instance?.destroy();
        } catch (_) {}
      });
      cardsSortableRefs.current = {};
    };
  }, []);

  useEffect(() => {
    if (!tabsTbodyRef.current) return;
    if (tabsSortableRef.current) {
      try {
        tabsSortableRef.current.destroy();
      } catch (_) {}
      tabsSortableRef.current = null;
    }
    const tableBody = tabsTbodyRef.current;
    if (!tableBody || !canEdit) return;
    tabsSortableRef.current = Sortable.create(tableBody, {
      animation: 150,
      handle: ".drag-handle",
      draggable: ".tab-row",
      onEnd: () => {
        const currentBody = tabsTbodyRef.current;
        if (!currentBody) return;
        const nodes = Array.from(
          currentBody.querySelectorAll<HTMLTableRowElement>(".tab-row")
        );
        const orderedTabIds = nodes
          .map((n) => n.getAttribute("data-tab-id"))
          .filter((id): id is string => Boolean(id));
        if (orderedTabIds.length) {
          reorderTabsMutation.mutate(orderedTabIds);
        }
      },
    });
    return () => {
      try {
        tabsSortableRef.current?.destroy();
      } catch (_) {}
      tabsSortableRef.current = null;
    };
  }, [entityType, activeSportId, tabsList.length, canEdit]);

  const bindCardsSortable =
    (tabId: string) => (el: HTMLTableSectionElement | null) => {
      const existing = cardsSortableRefs.current[tabId];

      if (!el) {
        if (existing) {
          try {
            existing.destroy();
          } catch (_) {}
          delete cardsSortableRefs.current[tabId];
        }
        return;
      }

      if (existing) {
        try {
          existing.destroy();
        } catch (_) {}
        delete cardsSortableRefs.current[tabId];
      }

      if (!canEdit) return;

      cardsSortableRefs.current[tabId] = Sortable.create(el, {
        animation: 150,
        handle: ".drag-handle",
        draggable: ".card-row",
        onEnd: () => {
          const nodes = Array.from(
            el.querySelectorAll<HTMLTableRowElement>(".card-row")
          );
          const orderedMarketIds = nodes
            .map((n) => n.getAttribute("data-market-id"))
            .filter((id): id is string => Boolean(id));
          if (orderedMarketIds.length) {
            const currentOrder = (cardsByTabId.get(tabId) ?? []).map((card) =>
              String(card.market_id)
            );
            const isSameOrder =
              currentOrder.length === orderedMarketIds.length &&
              currentOrder.every((id, index) => id === orderedMarketIds[index]);
            if (!isSameOrder) {
              reorderCardsMutation.mutate({ tabId, orderedMarketIds });
            }
          }
        },
      });
    };

  const handleOpenAddCard = useCallback((tabId: string) => {
    setTargetTabForAddCard(tabId);
    setSelectedCardsTabId(tabId);
    setShowAddCardModal(true);
    setAddMarketQuery("");
    setAddMarketSelected(null);
    setDebouncedMarketQuery("");
  }, []);

  const closeEditTabModal = () => {
    if (tabTranslationEditor.isMutating) return;
    if (tabTranslationEditor.hasDirtyTranslations) {
      tabTranslationEditor.showUnsavedChangesError();
      return;
    }
    setEditTabId(null);
    setEditTabName("");
    setOriginalEditTabName("");
    tabTranslationEditor.reset();
  };

  const handleEditTab = async () => {
    const tabName = editTabName.trim();
    if (
      !editTabId ||
      !tabName ||
      updateTabMutation.isPending ||
      tabTranslationEditor.isMutating ||
      tabTranslationEditor.hasEmptyTranslationDraft
    ) {
      return;
    }

    const dirtyValues = tabTranslationEditor.dirtyTranslationValues;
    const translationChanges =
      tabTranslationEditor.entityKey && Object.keys(dirtyValues).length > 0
        ? {
            translation_key: tabTranslationEditor.entityKey,
            translations: dirtyValues,
          }
        : undefined;

    if (tabName === originalEditTabName && !translationChanges) {
      setEditTabId(null);
      setEditTabName("");
      setOriginalEditTabName("");
      tabTranslationEditor.reset();
      return;
    }

    updateTabMutation.mutate({
      tabId: editTabId,
      tabName,
      translationChanges,
    });
  };

  const closeRenameMarketModal = () => {
    setShowRenameMarketModal(false);
    setRenameMarketPayload(null);
    setRenameMarketEntityKey(undefined);
  };

  const handleRenameCard: MarketCardRowHandler = useCallback(
    (card) => {
      if (!activeSportId) return;
      if (card.tab_id) setSelectedCardsTabId(card.tab_id);
      setRenameMarketPayload({
        sport_id: activeSportId,
        market_id: card.market_id,
        current_display_name: translationsEnabled
          ? getListEntityBaseName(card, card.display_name)
          : card.display_name,
        translations: card.translations,
        entity_id: card.entity_id,
      });
      setRenameMarketEntityKey(
        card.translations?.key ?? card.entity_id
      );
      setShowRenameMarketModal(true);
    },
    [activeSportId, translationsEnabled]
  );

  const handleDeleteCard: MarketCardRowHandler = useCallback((card) => {
    if (card.tab_id) setSelectedCardsTabId(card.tab_id);
    setDeleteCardTarget({
      tabId: card.tab_id,
      marketId: card.market_id,
    });
  }, []);

  const renderSportsTabs = (): React.ReactElement => (
    <>
      <div className="em-sports-header">
        <div className="em-sports-list">
          {(sportsQuery.data ?? []).map((s) => (
            <div
              key={s.id}
              className={`em-sport-pill ${
                activeSportId === s.id ? "active" : ""
              }`}
              onClick={() => setActiveSportId(s.id)}
            >
              <img src={ClipPath} alt={AppStrings.SPORT} />
              <span>{s.name}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="em-entity-type-toggle-outside">
        <div className="em-entity-type-toggle">
          <button
            className={entityType === ENTITY_TYPES.PREMATCH ? "active" : ""}
            onClick={() => {
              setEntityType(ENTITY_TYPES.PREMATCH);
            }}
          >
            {AppStrings.PRE_MATCH}
          </button>
          <button
            className={entityType === ENTITY_TYPES.LIVE ? "active" : ""}
            onClick={() => {
              setEntityType(ENTITY_TYPES.LIVE);
            }}
          >
            {AppStrings.LIVE}
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="event-markets">
      {renderSportsTabs()}

      <div className="em-section-header">
        <h3>{AppStrings.MARKET_TABS}</h3>
        {canEdit && (
          <button className="em-primary" onClick={() => setShowAddTabModal(true)}>
            {`+ ${AppStrings.ADD_TAB}`}
          </button>
        )}
      </div>
      <p className="em-section-subtitle">
        {AppStrings.MANAGE_THE_TAB_TITLES_DISPLAYED}
      </p>

      <div className="em-table-wrapper">
        <table className="em-table">
          <thead>
            <tr>
              <th>{`# ${AppStrings.ORDER}`}</th>
              <th>{AppStrings.TAB_NAME}</th>
              <th>{AppStrings.MARKETS}</th>
              <th>{AppStrings.ACTIONS}</th>
            </tr>
          </thead>
          <tbody ref={tabsTbodyRef}>
            {tabsList.length === 0 ? (
              <tr className="em-empty-row">
                <td colSpan={4} className="em-empty">
                  {`${AppStrings.NO_MARKET_TABS_FOUND_FOR_THIS} ${entityTypeLabelLower} ${activeSportName}`}
                </td>
              </tr>
            ) : (
              tabsList.map((tab, idx) => {
                const tabId = tab.tab_id;
                return (
                  <tr
                    key={tab.entity_id}
                    className={`tab-row${
                      selectedCardsTabId === tabId ? " selected" : ""
                    }`}
                    data-tab-id={tabId}
                    onClick={() => setSelectedCardsTabId(tabId)}
                  >
                    <td className="order-cell">
                      <div className="order-cell-inner">
                        <img
                          className="drag-handle"
                          src={ClipPath}
                          alt={AppStrings.DRAG}
                        />
                        <div className="order-pill">{idx + 1}</div>
                      </div>
                    </td>
                    <td>{tab.name}</td>
                    <td>{tab.card_count}</td>
                    <td className="actions">
                      {canEdit && (
                        <>
                          <button
                            className="em-icon-btn"
                            title={AppStrings.ADD_MARKET_TO_TAB}
                            onClick={() => handleOpenAddCard(tabId)}
                          >
                            <Plus size={14} />
                          </button>
                          <button
                            className="em-icon-btn"
                            title={AppStrings.EDIT_MARKET_TAB}
                            onClick={() => {
                              const initialName = translationsEnabled
                                ? getListEntityBaseName(tab, tab.name)
                                : tab.name;
                              setEditTabId(tabId);
                              setEditTabName(initialName);
                              setOriginalEditTabName(initialName);
                              tabTranslationEditor.reset();
                            }}
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            className="em-icon-btn danger"
                            title={AppStrings.DELETE_TAB}
                            onClick={() => setDeleteTabId(tabId)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="em-section-header em-tab-cards-intro">
        <div>
          <h3>{AppStrings.MARKETS_GAME_CARDS}</h3>
          <p>{AppStrings.MANAGE_MARKET_CARD_TITLES_AND_THEIR_PLACEMENT}</p>
        </div>
        <span />
      </div>

      {tabsList.map((tab) => {
        const tabCards = cardsByTabId.get(tab.tab_id) ?? [];
        const tabId = tab.tab_id;
        return (
          <div key={`${tab.entity_id}-cards`} className="em-tab-cards-section">
            <div className="em-section-header">
              <h4 className="em-tab-name">{tab.name}</h4>
              {canEdit && (
                <button
                  className="em-primary"
                  onClick={() => handleOpenAddCard(tabId)}
                >
                  {AppStrings.ADD_MARKET_GAME_CARD}
                </button>
              )}
            </div>
            <div className="em-table-wrapper">
              <table className="em-subtable-table em-cards-table">
                <thead>
                  <tr>
                    <th>{`# ${AppStrings.ORDER}`}</th>
                    <th>{AppStrings.ID}</th>
                    <th>{AppStrings.DISPLAY_MARKET_NAME}</th>
                    <th>{AppStrings.ORIGINAL_NAME}</th>
                    <th>{AppStrings.ACTIONS}</th>
                  </tr>
                </thead>
                <tbody ref={bindCardsSortable(tabId)}>
                  <CardsTableRows
                    cards={tabCards}
                    isLoading={
                      allCardsQuery.isLoading || allCardsQuery.isFetching
                    }
                    onRename={handleRenameCard}
                    onDelete={handleDeleteCard}
                    canEdit={canEdit}
                  />
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      <Modal
        closable={false}
        footer={null}
        width={600}
        open={showAddTabModal}
        onCancel={() => setShowAddTabModal(false)}
      >
        <div className="add-new-ip-panel">
          <div className="modal-header">
            <div className="modal-header-title">
              <h2 className="modal-title">{AppStrings.ADD_MARKET_TAB}</h2>
            </div>
            <X
              onClick={() => setShowAddTabModal(false)}
              className="close-icon"
            />
          </div>
          <div className="divider" />
          <div className="msb-modal-body">
            <label htmlFor="tabName">{AppStrings.TAB_NAME}</label>
            <Input
              id="tabName"
              placeholder={AppStrings.ENTER_TAB_NAME}
              value={newTabName}
              onChange={(e) => setNewTabName(e.target.value)}
            />
          </div>
          <div className="modal-footer">
            <button
              className="cancel-button"
              onClick={() => setShowAddTabModal(false)}
              disabled={createTabMutation.isPending}
            >
              {AppStrings.CLOSE}
            </button>
            <button
              className={`submit-button ${
                !newTabName.trim() || createTabMutation.isPending
                  ? "disabled"
                  : ""
              }`}
              disabled={!newTabName.trim() || createTabMutation.isPending}
              onClick={() => createTabMutation.mutate(newTabName.trim())}
            >
              {createTabMutation.isPending
                ? AppStrings.CREATING___
                : AppStrings.CREATE_BUTTON}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        closable={false}
        footer={null}
        width={480}
        className="future-tab-edit-modal"
        open={!!editTabId}
        onCancel={closeEditTabModal}
      >
        <div className="add-new-ip-panel future-tab-edit-panel">
          <div className="modal-header">
            <div className="modal-header-title">
              <h2 className="modal-title">{AppStrings.EDIT_MARKET_TAB}</h2>
            </div>
            <X
              onClick={closeEditTabModal}
              className="close-icon"
              aria-label={AppStrings.SPORTSBOOK_FM_CLOSE}
            />
          </div>
          <div className="divider" />
          <div className="msb-modal-body">
            <div className="future-tab-edit-field">
              <label htmlFor="editTabName">{AppStrings.TAB_NAME}</label>
              <Input
                id="editTabName"
                placeholder={AppStrings.ENTER_TAB_NAME}
                value={editTabName}
                onChange={(e) => setEditTabName(e.target.value)}
              />
            </div>
            <TranslationField editor={tabTranslationEditor} />
          </div>
          <div className="modal-footer">
            <button
              className="cancel-button"
              onClick={closeEditTabModal}
              disabled={updateTabMutation.isPending}
            >
              {AppStrings.CLOSE}
            </button>
            <button
              className={`submit-button ${
                !editTabName.trim() ||
                updateTabMutation.isPending ||
                tabTranslationEditor.isMutating ||
                tabTranslationEditor.hasEmptyTranslationDraft
                  ? "disabled"
                  : ""
              }`}
              disabled={
                !editTabName.trim() ||
                updateTabMutation.isPending ||
                tabTranslationEditor.isMutating ||
                tabTranslationEditor.hasEmptyTranslationDraft
              }
              onClick={handleEditTab}
            >
              {updateTabMutation.isPending
                ? AppStrings.SAVING
                : AppStrings.SAVE}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        closable={false}
        footer={null}
        width={600}
        open={showAddCardModal}
        onCancel={() => {
          setShowAddCardModal(false);
          setTargetTabForAddCard(null);
          setAddMarketQuery("");
          setAddMarketSelected(null);
          setDebouncedMarketQuery("");
        }}
      >
        <div className="add-new-ip-panel">
          <div className="modal-header">
            <div className="modal-header-title">
              <h2 className="modal-title">
                {AppStrings.ADD_MARKET_GAME_CARD}
              </h2>
            </div>
            <X
              onClick={() => setShowAddCardModal(false)}
              className="close-icon"
            />
          </div>
          <div className="divider" />
          <div className="msb-modal-body">
            <div className="autocomplete">
              <Input
                id="marketSearch"
                placeholder={AppStrings.START_TYPING_TO_SEARCH}
                value={addMarketQuery}
                onChange={(e) => setAddMarketQuery(e.target.value)}
              />
              {showAutocompleteDropdown ? (
                <div className="results">
                  {addMarketLoading ? (
                    <div className="center">
                      <Spin size="small" />
                    </div>
                  ) : (
                    addMarketOptions.map((opt) => (
                      <div
                        key={opt.entity_id}
                        className={`result-item ${
                          addMarketSelected?.entity_id === opt.entity_id
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => setAddMarketSelected(opt)}
                      >
                        <div className="result-main">
                          <span className="result-name">{opt.item_name}</span>
                          <Tag color="gold">
                            {AppStrings.MARKET.toUpperCase()}
                          </Tag>
                        </div>
                      </div>
                    ))
                  )}
                  {!addMarketLoading &&
                    addMarketOptions.length === 0 &&
                    debouncedMarketQuery.length >= 2 && (
                      <div className="empty-mini">
                        {AppStrings.NO_RESULTS}
                      </div>
                    )}
                </div>
              ) : null}
            </div>
            {addMarketError && debouncedMarketQuery.length >= 2 ? (
              <div className="error-msg">{addMarketError}</div>
            ) : null}
          </div>
          <div className="modal-footer">
            <button
              className="cancel-button"
              onClick={() => {
                setShowAddCardModal(false);
                setTargetTabForAddCard(null);
                setAddMarketQuery("");
                setAddMarketSelected(null);
                setDebouncedMarketQuery("");
              }}
              disabled={createCardMutation.isPending}
            >
              {AppStrings.CLOSE}
            </button>
            <button
              className={`submit-button ${
                !addMarketSelected || createCardMutation.isPending
                  ? "disabled"
                  : ""
              }`}
              disabled={!addMarketSelected || createCardMutation.isPending}
              onClick={() =>
                targetTabForAddCard &&
                addMarketSelected &&
                createCardMutation.mutate({
                  tabId: targetTabForAddCard,
                  marketId: addMarketSelected.market_id,
                })
              }
            >
              {createCardMutation.isPending
                ? AppStrings.ADDING___
                : AppStrings.ADD}
            </button>
          </div>
        </div>
      </Modal>

      <RenameEntityModal
        open={showRenameMarketModal}
        title={AppStrings.EDIT_MARKET_NAME}
        nameLabel={AppStrings.DISPLAY_NAME}
        initialName={renameMarketPayload?.current_display_name || ""}
        entityKey={renameMarketEntityKey}
        initialTranslationData={renameMarketTranslationData}
        translationsEnabled={translationsEnabled}
        bundleTranslationChanges={translationsEnabled}
        saving={renameMarketMutation.isPending}
        onSave={async (name, translationChanges) => {
          if (!renameMarketPayload?.market_id) return;
          try {
            await renameMarketMutation.mutateAsync({
              marketId: renameMarketPayload.market_id,
              newDisplayName: name,
              translationChanges,
            });
          } catch {
            return;
          }
        }}
        onClose={closeRenameMarketModal}
      />

      <Modal
        closable={false}
        footer={null}
        width={500}
        open={!!deleteTabId}
        onCancel={() => setDeleteTabId(null)}
      >
        <div className="add-new-ip-panel">
          <div className="modal-header">
            <div className="modal-header-title">
              <h2 className="modal-title">{AppStrings.DELETE_MARKET_TAB}</h2>
            </div>
            <X onClick={() => setDeleteTabId(null)} className="close-icon" />
          </div>
          <div className="divider" />
          <div className="msb-modal-body">
            <p>
              {AppStrings.ARE_YOU_SURE_YOU_WANT_TO_DELETE_MARKET_TAB.replace(
                "{tabName}",
                deleteTabName || AppStrings.THIS_TAB
              )}
            </p>
          </div>
          <div className="modal-footer">
            <button
              className="cancel-button"
              onClick={() => setDeleteTabId(null)}
              disabled={deleteTabMutation.isPending}
            >
              {AppStrings.CLOSE}
            </button>
            <button
              className={`submit-button delete-button ${
                deleteTabMutation.isPending ? "disabled" : ""
              }`}
              onClick={() =>
                deleteTabId && deleteTabMutation.mutate(deleteTabId)
              }
              disabled={deleteTabMutation.isPending}
            >
              {deleteTabMutation.isPending
                ? AppStrings.DELETING___
                : AppStrings.DELETE}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        closable={false}
        footer={null}
        width={500}
        open={!!deleteCardTarget}
        onCancel={() => setDeleteCardTarget(null)}
      >
        <div className="add-new-ip-panel">
          <div className="modal-header">
            <div className="modal-header-title">
              <h2 className="modal-title">
                {`${AppStrings.DELETE} ${AppStrings.MARKET}`}
              </h2>
            </div>
            <X
              onClick={() => setDeleteCardTarget(null)}
              className="close-icon"
            />
          </div>
          <div className="divider" />
          <div className="msb-modal-body">
            <p>
              {
                AppStrings.ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_MARKET_FROM_THE_TAB
              }
            </p>
          </div>
          <div className="modal-footer">
            <button
              className="cancel-button"
              onClick={() => setDeleteCardTarget(null)}
              disabled={deleteCardMutation.isPending}
            >
              {AppStrings.CLOSE}
            </button>
            <button
              className={`submit-button delete-button ${
                deleteCardMutation.isPending ? "disabled" : ""
              }`}
              onClick={() =>
                deleteCardTarget && deleteCardMutation.mutate(deleteCardTarget)
              }
              disabled={deleteCardMutation.isPending}
            >
              {deleteCardMutation.isPending
                ? AppStrings.DELETING___
                : AppStrings.DELETE}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function CardsTableRows({
  cards,
  isLoading,
  onRename,
  onDelete,
  canEdit = true,
}: CardsTableRowsProps): React.ReactElement {
  if (isLoading) {
    return (
      <tr className="em-empty-row">
        <td colSpan={5} className="em-empty">
          {AppStrings.LOADING_MARKETS___}
        </td>
      </tr>
    );
  }

  if (!cards || cards.length === 0) {
    return (
      <tr className="em-empty-row">
        <td colSpan={5} className="em-empty">
          {AppStrings.THERE_ARE_NO_MARKETS_FOR_THIS_MARKET_TAB}
        </td>
      </tr>
    );
  }

  return (
    <>
      {cards.map((card, idx) => {
        const displayOrder = cardRowDisplayOrder(card, idx);
        return (
          <tr
            className="card-row"
            key={card.entity_id}
            data-id={card.entity_id}
            data-market-id={String(card.market_id)}
            data-tab-id={card.tab_id}
            title={
              card.tab_name
                ? `${AppStrings.TAB}: ${card.tab_name}`
                : undefined
            }
          >
            <td className="order-cell">
              <div className="order-cell-inner">
                <img
                  className="drag-handle"
                  src={ClipPath}
                  alt={AppStrings.DRAG}
                />
                <div className="order-pill">{displayOrder}</div>
              </div>
            </td>
            <td>{card.market_id}</td>
            <td>{card.display_name}</td>
            <td>{card.name}</td>
            <td className="actions">
              {canEdit && (
                <>
                  <button
                    className="em-icon-btn"
                    title={AppStrings.EDIT_MARKET_NAME}
                    onClick={() => onRename(card)}
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    className="em-icon-btn danger"
                    title={`${AppStrings.DELETE} ${AppStrings.MARKET}`}
                    onClick={() => onDelete(card)}
                  >
                    <Trash2 size={14} />
                  </button>
                </>
              )}
            </td>
          </tr>
        );
      })}
    </>
  );
}

export default EventMarkets;
